/**
 * CreateSessionModal.tsx
 * Modal-based session creation — replaces the full-page ScenarioDetail / SessionCreation route.
 *
 * Uses ValidateContext — no props needed.
 * Called by: onStartNewSession (hook) → opens isCreateSessionModalOpen
 *
 * Fields:
 *   Row 1: Name (text input) | Team (same combobox from ScenarioDetail)
 *   Row 2: Setup | Testing Environment
 *
 * API integration:
 *   – testingEnvironmentOptions populated from context (fetched when modal opens).
 *   – When session-config API ships, only swap the stub in validate.api.ts; no change here.
 *   – testingEnvironment value is included in createSession payload.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { components } from 'react-select';
import './CreateSetupModal.scss';   // reuse same modal SCSS — identical panel design
import CustomSelect from '../../CommonComponents/CustomSelect';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import { CrossSvg } from '../../CommonComponents/svgs/Svgs';
import { showToastMessage } from '../../../utils/commonFunctions';
import { createSession, extractApiError, getTestPlans } from '../api/validate.api';
import { setupHasRunningSession } from '../helpers/validateDetails.helpers';
import { useValidateState, useValidateActions } from '../context/ValidateContext';
import type { SelectOption, TestPlan } from '../types/validateDetails.types';
import {
  SESSION_NAME_MAX_LEN,
  SESSION_TEAM_MAX_LEN,
  CHAR_LIMIT_TOAST_COOLDOWN_MS,
} from '../constants/validateDetails.constants';

// ─── Shared select styling ─────────────────────────────────────────────────────

const ERROR_BORDER = '0.5px solid rgba(255, 100, 80, 0.6)';

// ─── Form ─────────────────────────────────────────────────────────────────────

type SetupSelectOption = {
  label: string;
  value: string;
  setupType?: string;
  isDisabled?: boolean;
  isBusy?: boolean;
};

type TestPlanSelectOption = SelectOption<string>;

type FormValues = {
  name:               string;
  teamValue:          string;
  setup:              SetupSelectOption | null;
  testingEnvironment: { label: string; value: string } | null;
  setupSanityTestPlan: TestPlanSelectOption | null;
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

const EMPTY_FORM: FormValues = {
  name:               '',
  teamValue:          '',
  setup:              null,
  testingEnvironment: null,
  setupSanityTestPlan: null,
};

function validateForm(values: FormValues, requireSetupSanityTestPlan: boolean): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim())          errors.name               = 'Name is required';
  if (!values.teamValue.trim())     errors.teamValue          = 'Team is required';
  if (!values.setup)                errors.setup              = 'Please select an available setup';
  if (!values.testingEnvironment)   errors.testingEnvironment = 'Please select a testing environment';
  if (requireSetupSanityTestPlan && !values.setupSanityTestPlan) {
    errors.setupSanityTestPlan = 'Please select a setup sanity test plan';
  }
  return errors;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

function fireCharLimitToast(
  toastRef: React.MutableRefObject<Record<string, number>>,
  field: string,
  label: string,
  max: number,
): void {
  const now = Date.now();
  if ((toastRef.current[field] ?? 0) + CHAR_LIMIT_TOAST_COOLDOWN_MS > now) return;
  toastRef.current[field] = now;
  showToastMessage('info', `${label} cannot exceed ${max} characters`);
}

function normalizeSetupType(setupType?: string): string {
  const trimmed = setupType?.trim();
  return trimmed || 'UNKNOWN';
}

function isFlatsatSetupType(setupType?: string): boolean {
  return normalizeSetupType(setupType).toUpperCase() === 'FLATSAT';
}

function getSetupTypeLabel(setupType?: string): string {
  const normalized = normalizeSetupType(setupType).toUpperCase();
  if (normalized === 'FLATSAT') return 'FlatSat';
  if (normalized === 'TRUETWIN') return 'TrueTwin';
  if (normalized === 'SUBSYSTEM_UNIT_TESTING') return 'Subsystem';
  return 'Unknown';
}

function getSetupTooltip(data: SetupSelectOption): string {
  const typeLabel = getSetupTypeLabel(data.setupType);
  const fullType = normalizeSetupType(data.setupType).toUpperCase() === 'SUBSYSTEM_UNIT_TESTING' ? 'Subsystem Unit Testing' : typeLabel;
  const busyLabel = data.isBusy ? ' - setup busy' : '';
  return `${data.label} (${fullType})${busyLabel}`;
}

function SetupOptionContent({ data, single = false }: { data: SetupSelectOption; single?: boolean }) {
  const typeKey = normalizeSetupType(data.setupType).toLowerCase();
  return (
    <div className={`csm__setup-option${single ? ' csm__setup-option--single' : ''}${data.isDisabled ? ' csm__setup-option--disabled' : ''}`}>
      <span className="csm__setup-option__name">{data.label}</span>
      <span className="csm__setup-option__meta">
        <span className={`csm__setup-option__type csm__setup-option__type--${typeKey}`}>
          {getSetupTypeLabel(data.setupType)}
        </span>
        {data.isBusy && <span className="csm__setup-option__busy">• setup busy</span>}
      </span>
    </div>
  );
}

function SetupOption(props: any) {
  const { data } = props as { data: SetupSelectOption };
  return (
    <components.Option {...props}>
      <CustomToolTip
        title={getSetupTooltip(data)}
        maxWidth="320px"
        placement="right"
        wordBreak="break-word"
        className="csm__setup-tooltip"
      >
        <SetupOptionContent data={data} />
      </CustomToolTip>
    </components.Option>
  );
}

function SetupSingleValue(props: any) {
  const { data } = props as { data: SetupSelectOption };
  return (
    <components.SingleValue {...props}>
      <CustomToolTip
        title={getSetupTooltip(data)}
        maxWidth="320px"
        placement="top"
        wordBreak="break-word"
        className="csm__setup-tooltip"
      >
        <SetupOptionContent data={data} single />
      </CustomToolTip>
    </components.SingleValue>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateSessionModal = () => {
  const {
    mission_id,
    selectedSatellite,
    filteredSetups,
    testingEnvironmentOptions,
    loadingSessionConfig,
    isCreateSessionModalOpen: open,
    activeSetup,
  } = useValidateState();

  const {
    closeCreateSessionModal: onClose,
    startDeployPolling,
    addPendingSession,
    refreshSessionsForSatellite,
  } = useValidateActions();

  const [values,    setValues]    = useState<FormValues>(EMPTY_FORM);
  const [errors,    setErrors]    = useState<FormErrors>({});
  const [deploying, setDeploying] = useState(false);
  const [testPlanOptions, setTestPlanOptions] = useState<TestPlanSelectOption[]>([]);
  const [loadingTestPlans, setLoadingTestPlans] = useState(false);

  // Char-limit toast cooldown (prevents toast spam)
  const charLimitToastRef = useRef<Record<string, number>>({});

  // ── Teams combobox state (same pattern as ScenarioDetail) ─────────────────
  const [allTeams,         setAllTeams]         = useState<string[]>([]);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [teamHighlightIdx, setTeamHighlightIdx] = useState(0);
  const teamInputRef   = useRef<HTMLInputElement>(null);
  const teamWrapperRef = useRef<HTMLDivElement>(null);

  const teamFilteredOptions = useMemo(
    () => allTeams.filter((t) => t.toLowerCase().includes(values.teamValue.toLowerCase())),
    [allTeams, values.teamValue],
  );
  const teamShowAddNew =
    values.teamValue.trim() !== '' &&
    !allTeams.some((t) => t.toLowerCase() === values.teamValue.trim().toLowerCase());
  const teamTotalItems = teamFilteredOptions.length + (teamShowAddNew ? 1 : 0);

  const satelliteGroupId = selectedSatellite?.value?.groupId ?? '';
  const satelliteId      = selectedSatellite?.value?.satId ?? '';
  const selectedSetupId  = values.setup?.value ?? '';
  const selectedSetupIsFlatsat = isFlatsatSetupType(values.setup?.setupType);
  const selectedSetupIsTrueTwin = values.setup?.setupType === 'TRUETWIN';
  const showSetupSanityTestPlan = Boolean(values.setup && (selectedSetupIsFlatsat || selectedSetupIsTrueTwin));
  const requiresSetupSanityTestPlan = Boolean(values.setup && selectedSetupIsFlatsat);

  const setField = useCallback(
    <K extends keyof FormValues>(key: K, val: FormValues[K]) => {
      if (key === 'name' && typeof val === 'string' && val.length > SESSION_NAME_MAX_LEN) {
        fireCharLimitToast(charLimitToastRef, 'name', 'Session name', SESSION_NAME_MAX_LEN);
        return;
      }
      setValues((prev) => ({ ...prev, [key]: val }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    },
    [],
  );

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setValues(EMPTY_FORM);
      setAllTeams([]);
      setErrors({});
      setTestPlanOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !mission_id || !satelliteId || !showSetupSanityTestPlan) return;
    let cancelled = false;

    setLoadingTestPlans(true);
    setErrors((prev) => ({ ...prev, setupSanityTestPlan: undefined }));
    getTestPlans(mission_id, satelliteId)
      .then(({ data, apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setErrors((prev) => ({ ...prev, setupSanityTestPlan: apiError }));
        }
        setTestPlanOptions(
          data.map((plan: TestPlan) => ({
            label: plan.testPlanName,
            value: plan.testPlanId,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingTestPlans(false);
      });

    return () => { cancelled = true; };
  }, [open, mission_id, satelliteId, showSetupSanityTestPlan]);

  useEffect(() => {
    if (!open || !values.setup || selectedSetupIsFlatsat || selectedSetupIsTrueTwin) return;
    setValues((prev) => prev.setupSanityTestPlan ? { ...prev, setupSanityTestPlan: null } : prev);
    setErrors((prev) => prev.setupSanityTestPlan ? { ...prev, setupSanityTestPlan: undefined } : prev);
    setLoadingTestPlans(false);
  }, [open, values.setup, selectedSetupIsFlatsat, selectedSetupIsTrueTwin]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close teams dropdown on outside click — only active when modal is open
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (teamWrapperRef.current && !teamWrapperRef.current.contains(e.target as Node)) {
        setTeamDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  // Auto-select when only one testing environment is available
  useEffect(() => {
    if (!open) return;
    if (!values.testingEnvironment && testingEnvironmentOptions.length === 1) {
      setField('testingEnvironment', testingEnvironmentOptions[0]);
    }
  }, [open, testingEnvironmentOptions, values.testingEnvironment, setField]);

  const setupOptions = useMemo<SetupSelectOption[]>(
    () => filteredSetups.map((setup) => {
      const isBusy = setupHasRunningSession(setup);
      return {
        label: setup.setupName,
        value: setup.setupId,
        setupType: setup.setupType,
        isDisabled: isBusy,
        isBusy,
      };
    }),
    [filteredSetups],
  );

  const preferredSetupOption = useMemo<SetupSelectOption | null>(() => {
    const active = setupOptions.find((option) => option.value === activeSetup?.setupId && !option.isDisabled);
    return active ?? setupOptions.find((option) => !option.isDisabled) ?? null;
  }, [setupOptions, activeSetup?.setupId]);

  const canDeploy = Boolean(
    mission_id && satelliteGroupId && satelliteId && selectedSetupId && !values.setup?.isDisabled
    && values.name.trim() && values.teamValue.trim() && values.testingEnvironment
    && (!requiresSetupSanityTestPlan || values.setupSanityTestPlan),
  );

  const deployDisabledReason =
    !satelliteGroupId || !satelliteId
      ? 'Select a satellite first'
      : !values.setup
      ? 'Select an available setup'
      : values.setup.isDisabled
      ? 'Busy setups cannot be selected'
      : requiresSetupSanityTestPlan && !values.setupSanityTestPlan
      ? 'Select setup sanity test plan'
      : undefined;

  useEffect(() => {
    if (!open) return;
    const currentSetupOption = setupOptions.find((option) => option.value === values.setup?.value && !option.isDisabled);
    if (values.setup && currentSetupOption) {
      if (currentSetupOption !== values.setup) setField('setup', currentSetupOption);
      return;
    }
    if (preferredSetupOption) setField('setup', preferredSetupOption);
  }, [open, values.setup, setupOptions, preferredSetupOption, setField]);

  // ── Teams combobox handlers (identical logic to ScenarioDetail) ────────────
  const handleTeamSelect = useCallback((name: string) => {
    const trimmed = name.trim().toUpperCase();
    if (!trimmed) return;
    setAllTeams((prev) => prev.some((t) => t === trimmed) ? prev : [...prev, trimmed]);
    setField('teamValue', trimmed);
    setTeamDropdownOpen(false);
    setTeamHighlightIdx(0);
    teamInputRef.current?.focus();
  }, [setField]);

  const handleTeamInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > SESSION_TEAM_MAX_LEN) {
      fireCharLimitToast(charLimitToastRef, 'teamValue', 'Team name', SESSION_TEAM_MAX_LEN);
      return;
    }
    setField('teamValue', val);
    setTeamDropdownOpen(true);
    setTeamHighlightIdx(0);
  }, [setField]);

  const handleTeamKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!teamDropdownOpen && e.key !== 'Escape') setTeamDropdownOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setTeamHighlightIdx((i) => (i + 1) % Math.max(teamTotalItems, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setTeamHighlightIdx((i) => (i - 1 + Math.max(teamTotalItems, 1)) % Math.max(teamTotalItems, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (teamTotalItems === 0) return;
      if (teamHighlightIdx < teamFilteredOptions.length) {
        handleTeamSelect(teamFilteredOptions[teamHighlightIdx]);
      } else if (teamShowAddNew) {
        handleTeamSelect(values.teamValue);
      }
    } else if (e.key === 'Escape') {
      setTeamDropdownOpen(false);
    } else {
      setTeamHighlightIdx(0);
    }
  }, [teamDropdownOpen, teamTotalItems, teamHighlightIdx, teamFilteredOptions, teamShowAddNew, values.teamValue, handleTeamSelect]);

  const onOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleDeploy = useCallback(async () => {
    if (!canDeploy) return;
    const errs = validateForm(values, requiresSetupSanityTestPlan);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setDeploying(true);

    try {
      const res = await createSession(mission_id, satelliteGroupId, satelliteId, {
        name:               values.name.trim(),
        testingEnvironment: values.testingEnvironment!.value,
        setupId:            values.setup!.value,
        teams:              [values.teamValue.trim().toUpperCase()],
        setupSanityTestPlanId: values.setupSanityTestPlan?.value,
      });
      showToastMessage('success', res?.message?.trim() || 'Deployment in progress');
      addPendingSession({
        sessionName: values.name.trim(),
        teamName: values.teamValue.trim().toUpperCase(),
        testingEnvironment: values.testingEnvironment!.label,
        setupId: values.setup!.value,
        setupName: values.setup!.label,
      });
      onClose();
      startDeployPolling(values.setup!.value);
      void refreshSessionsForSatellite();
    } catch (err: unknown) {
      const message = extractApiError(err, 'Something went wrong. Please try again.');
      showToastMessage('error', message);
    } finally {
      setDeploying(false);
    }
  }, [
    canDeploy,
    mission_id,
    satelliteGroupId,
    satelliteId,
    values,
    requiresSetupSanityTestPlan,
    onClose,
    startDeployPolling,
    addPendingSession,
    refreshSessionsForSatellite,
  ]);

  if (!open) return null;

  return (
    <div
      className="csm__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="csm-session-title"
      onClick={onOverlayClick}
    >
      <div className="csm__panel" data-testid="create-session-modal">
        {/* ── Header ── */}
        <div className="csm__header">
          <div className="csm__header__row">
            <h2 className="csm__header__title" id="csm-session-title">Session Config</h2>
            <button
              className="csm__header__close"
              type="button"
              aria-label="Close"
              onClick={onClose}
              data-testid="create-session-close-btn"
            >
              <CrossSvg />
            </button>
          </div>
          <div className="csm__header__divider" />
        </div>

        {/* ── Body ── */}
        <div className="csm__body">

          {/* Row 1 — Name | Team */}
          <div className="csm__row">
            <div className="csm__field">
              <label className="csm__field__label" htmlFor="csm-session-name">Name*</label>
              <input
                id="csm-session-name"
                className={`csm__field__input${errors.name ? ' csm__field__input--error' : ''}`}
                type="text"
                placeholder="Session name"
                value={values.name}
                onChange={(e) => setField('name', e.target.value)}
                autoComplete="off"
                data-testid="create-session-name-input"
              />
              {errors.name && <span className="csm__error">{errors.name}</span>}
            </div>

            {/* Team — same combobox pattern from ScenarioDetail */}
            <div className="csm__field">
              <label className="csm__field__label" htmlFor="csm-session-team">Team*</label>
              <div
                className={`csm__teams-combobox${errors.teamValue ? ' csm__teams-combobox--error' : ''}`}
                ref={teamWrapperRef}
              >
                <input
                  ref={teamInputRef}
                  id="csm-session-team"
                  type="text"
                  className={`csm__field__input csm__field__input--teams${errors.teamValue ? ' csm__field__input--error' : ''}`}
                  placeholder="Search or add team…"
                  value={values.teamValue}
                  autoComplete="off"
                  onChange={handleTeamInputChange}
                  onFocus={() => setTeamDropdownOpen(true)}
                  onKeyDown={handleTeamKeyDown}
                  data-testid="create-session-team-input"
                />
                {teamDropdownOpen && teamTotalItems > 0 && (
                  <ul className="csm__teams-dropdown" role="listbox">
                    {teamFilteredOptions.map((team, idx) => {
                      const q = values.teamValue.toLowerCase();
                      const i = team.toLowerCase().indexOf(q);
                      return (
                        <li
                          key={team}
                          className={`csm__teams-dropdown__item${idx === teamHighlightIdx ? ' csm__teams-dropdown__item--highlighted' : ''}`}
                          role="option"
                          onMouseDown={(e) => { e.preventDefault(); handleTeamSelect(team); }}
                          onMouseEnter={() => setTeamHighlightIdx(idx)}
                        >
                          {(!q || i === -1) ? (
                            <span>{team}</span>
                          ) : (
                            <span>
                              {team.slice(0, i)}
                              <mark>{team.slice(i, i + q.length)}</mark>
                              {team.slice(i + q.length)}
                            </span>
                          )}
                        </li>
                      );
                    })}
                    {teamShowAddNew && (
                      <>
                        {teamFilteredOptions.length > 0 && (
                          <li className="csm__teams-dropdown__divider" role="separator" />
                        )}
                        <li
                          className={`csm__teams-dropdown__item csm__teams-dropdown__item--add${teamHighlightIdx === teamFilteredOptions.length ? ' csm__teams-dropdown__item--highlighted' : ''}`}
                          role="option"
                          onMouseDown={(e) => { e.preventDefault(); handleTeamSelect(values.teamValue); }}
                          onMouseEnter={() => setTeamHighlightIdx(teamFilteredOptions.length)}
                        >
                          <span className="csm__teams-dropdown__add-icon">+</span>
                          <span>Add &ldquo;<strong>{values.teamValue.trim().toUpperCase()}</strong>&rdquo;</span>
                        </li>
                      </>
                    )}
                  </ul>
                )}
                {teamDropdownOpen && teamTotalItems === 0 && values.teamValue.trim() === '' && allTeams.length === 0 && (
                  <ul className="csm__teams-dropdown" role="listbox">
                    <li className="csm__teams-dropdown__empty">Type to add a team</li>
                  </ul>
                )}
              </div>
              {errors.teamValue && <span className="csm__error">{errors.teamValue}</span>}
            </div>
          </div>

          {/* Row 2 — Setup | Testing Environment */}
          <div className="csm__row">
            <div className="csm__field">
              <span className="csm__field__label">Setup*</span>
              <div className={`csm__select${errors.setup ? ' csm__select--error' : ''}`}>
                <CustomSelect
                  options={setupOptions}
                  placeholder={setupOptions.length === 0 ? 'No setup available' : 'Select'}
                  value={values.setup}
                  onChange={(opt) => setField('setup', opt as SetupSelectOption | null)}
                  isSearchable={true}
                  isDisabled={setupOptions.length === 0}
                  controllHeight="34px"
                  padding="0px 4px"
                  border={errors.setup ? ERROR_BORDER : undefined}
                  background="rgba(10, 10, 10, 0.65)"
                  color="#f0f0f0"
                  fullWidth
                  overrideComponents={{ Option: SetupOption, SingleValue: SetupSingleValue }}
                />
              </div>
              {errors.setup && <span className="csm__error">{errors.setup}</span>}
            </div>

            <div className="csm__field">
              <span className="csm__field__label">Testing Environment*</span>
              <div className={`csm__select${errors.testingEnvironment ? ' csm__select--error' : ''}`}>
                <CustomSelect
                  options={testingEnvironmentOptions}
                  placeholder={loadingSessionConfig ? 'Loading…' : 'Select'}
                  value={values.testingEnvironment}
                  onChange={(opt) => setField('testingEnvironment', opt as { label: string; value: string } | null)}
                  isSearchable={true}
                  isDisabled={loadingSessionConfig}
                  controllHeight="34px"
                  padding="0px 4px"
                  border={errors.testingEnvironment ? ERROR_BORDER : undefined}
                  background="rgba(10, 10, 10, 0.65)"
                  color="#f0f0f0"
                  fullWidth
                />
              </div>
              {errors.testingEnvironment && (
                <span className="csm__error">{errors.testingEnvironment}</span>
              )}
            </div>
          </div>

          {/* Row 3 — Setup Sanity Test Plan */}
          {showSetupSanityTestPlan && (
            <div className="csm__field csm__field--full">
              <span className="csm__field__label">Setup Sanity Test Plan{selectedSetupIsTrueTwin ? ' (Optional)' : '*'}</span>
              <div className={`csm__select${errors.setupSanityTestPlan ? ' csm__select--error' : ''}`}>
                <CustomSelect
                  options={testPlanOptions}
                  placeholder={loadingTestPlans ? 'Loading...' : testPlanOptions.length === 0 ? 'No test plans available' : 'Select'}
                  value={values.setupSanityTestPlan}
                  onChange={(opt) => setField('setupSanityTestPlan', opt as TestPlanSelectOption | null)}
                  isSearchable
                  isDisabled={loadingTestPlans || testPlanOptions.length === 0}
                  controllHeight="34px"
                  padding="0px 4px"
                  border={errors.setupSanityTestPlan ? ERROR_BORDER : undefined}
                  background="rgba(10, 10, 10, 0.65)"
                  color="#f0f0f0"
                  fullWidth
                />
              </div>
              {errors.setupSanityTestPlan && (
                <span className="csm__error">{errors.setupSanityTestPlan}</span>
              )}
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="csm__footer">
          <div className="csm__footer__divider" />
          <div className="csm__footer__actions">
            <button
              type="button"
              className="csm__btn csm__btn--close"
              onClick={onClose}
              disabled={deploying}
              data-testid="create-session-cancel-btn"
            >
              Close
            </button>
            <button
              type="button"
              className={`csm__btn csm__btn--save${deploying ? ' csm__btn--saving' : ''}`}
              onClick={handleDeploy}
              disabled={deploying || !canDeploy}
              title={!canDeploy ? deployDisabledReason : undefined}
              data-testid="create-session-submit-btn"
            >
              {deploying ? 'Initiating...' : 'Initiate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSessionModal;
